import { inject, Injectable, signal } from '@angular/core';
import { OverlayService } from '@app/services/overlay/overlay.service';
import { SnackbarComponent } from '@app/components/shared/snackbar/snackbar.component';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';
import { IconName } from '@app/utils/enums/icon-enum';
import { Notification } from '@app/utils/interfaces/snackbar.interface';

const MAX_NOTIFICATIONS = 3;
const DURATION = 5000;

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  public notifications = signal<Notification[]>([]);
  protected readonly overlay = inject(OverlayService);
  private readonly timeouts = signal<Record<string, ReturnType<typeof setTimeout>>>({});

  public openSnackbar(status: SnackbarStatus, messageKey?: string): void {
    const id = crypto.randomUUID();

    const isSuccess = status === SnackbarStatus.SUCCESS;

    const newNotification: Notification = {
      id,
      messageKey:
        messageKey ?? (isSuccess ? 'snackbar.messages.success' : 'snackbar.messages.fail'),
      iconName: isSuccess ? IconName.CONFIRMATION_ICON : IconName.ERROR_ICON,
    };

    this.notifications.update((current) => {
      const updated = [newNotification, ...current];
      if (updated.length > MAX_NOTIFICATIONS) {
        updated.pop();
      }
      return updated;
    });

    this.overlay.attach(SnackbarComponent);

    const timeoutId = globalThis.setTimeout(() => this.removeNotification(id), DURATION);
    this.timeouts.update((current) => {
      const updated = { ...current, [id]: timeoutId };
      return updated;
    });
  }

  public removeNotification(id: string): void {
    const timeoutId = this.timeouts()[id];
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    this.timeouts.update((current) => {
      const { [id]: _, ...rest } = current;
      return rest;
    });

    this.notifications.update((current) => current.filter((n) => n.id !== id));

    if (this.notifications().length === 0) {
      this.overlay.detach();
    }
  }
}
